/**
 * The photo pipeline's browser half: take what the camera gave us, make it
 * something worth keeping and something worth showing.
 *
 * **Resizing happens here, before the upload, and it is the difference between
 * a fast capture and a slow one.** A modern phone photograph is four to eight
 * megabytes; the same picture at 2048px and quality 0.85 is around one, still
 * more than good enough to put on a listing (which is the constraint from the
 * brief: do not over-compress). Uploading the original would be four times the
 * bytes, four times the wait, and four times the storage for a photo nobody
 * will ever look at at full size.
 *
 * Two photos come out of every one that goes in: the full size, and a 400px
 * thumbnail. A grid of a hundred cards loads the second, which is why the grid
 * feels instant.
 *
 * ## Orientation
 *
 * A phone photograph is usually stored sideways with an EXIF tag saying which
 * way is up. Drawing it through an `<img>` element rather than decoding the
 * bytes ourselves is what applies that tag -- browsers default to
 * `image-orientation: from-image`, and `drawImage` honours it. Decode the file
 * any other way and every portrait photo in the basement ends up on its side.
 */

import { PHOTO_LONG_EDGE, PHOTO_QUALITY, THUMB_LONG_EDGE, THUMB_QUALITY } from "@iltocat/shared";

export interface PreparedPhoto {
  /** What gets stored and what a listing would use. */
  full: Blob;
  /** What a card shows. */
  thumb: Blob;
  /** The full size's dimensions, after any resize. */
  width: number;
  height: number;
  /** For the preview, before anything is uploaded. Revoke it when done. */
  previewUrl: string;
}

/**
 * The size an image becomes, never larger than it started.
 *
 * Upscaling a small photo would cost bytes and add nothing: the pixels are not
 * there. Pure, so it is the part of this file a test can actually check --
 * everything else needs a canvas.
 */
export function fitWithin(
  width: number,
  height: number,
  longEdge: number,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= longEdge || longest === 0) return { width, height };

  const scale = longEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/** Everything the app will ever do to one file the camera or the disk handed over. */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const image = await decode(file);
  try {
    const full = await render(image, PHOTO_LONG_EDGE, PHOTO_QUALITY);
    const thumb = await render(image, THUMB_LONG_EDGE, THUMB_QUALITY);
    return {
      full: full.blob,
      thumb: thumb.blob,
      width: full.width,
      height: full.height,
      // From the resized copy rather than the original: the preview is on
      // screen while several more photos are being taken, and holding four
      // full-size files in memory is how a phone browser gets killed.
      previewUrl: URL.createObjectURL(thumb.blob),
    };
  } finally {
    // An `<img>` holds its decoded bitmap until it is dropped, and a burst of
    // photos drops a lot of them.
    image.src = "";
  }
}

/** What the file picker should accept. Anything else is not a photo, whatever it is called. */
export const isImage = (file: File): boolean => file.type.startsWith("image/");

/* ------------------------------------------------------------- plumbing -- */

function decode(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("That file is not an image this browser can read."));
    };
    image.src = url;
  });
}

async function render(
  image: HTMLImageElement,
  longEdge: number,
  quality: number,
): Promise<{ blob: Blob; width: number; height: number }> {
  const size = fitWithin(image.naturalWidth, image.naturalHeight, longEdge);
  const canvas = document.createElement("canvas");
  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("This browser would not give utucat a canvas to resize photos on.");
  // JPEG has no transparency, and a canvas nobody painted is transparent black,
  // so a PNG cut-out would come out as a silhouette on black. White is what a
  // product shot sits on, and it goes into the bytes rather than the theme: a
  // listing photo has no dark mode.
  context.fillStyle = "white";
  context.fillRect(0, 0, size.width, size.height);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  context.drawImage(image, 0, 0, size.width, size.height);

  // JPEG rather than WebP: every marketplace, every phone and every desktop
  // takes it without a thought, and the worker refuses anything else so that a
  // key ending in .jpg is never a lie about its bytes.
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new Error("That photo could not be resized. Try another one.");

  return { blob, width: size.width, height: size.height };
}
